/**
 * The "How do you want to model your options?" chooser — ONE artifact for every module
 * (owner direction, 2026-08-25: "consistency of artifacts and UI where applicable"). The Aged
 * Care module set the pattern — each mode is a card with a title, a plain-words description of
 * what the mode actually does IN THIS MODULE, and an audience chip — and the other modules had
 * drifted to bare buttons that explained nothing. The copy is per-module (each mode does
 * different things in each module, and saying so is the point); the anatomy and styling are
 * shared here so they can never drift again.
 */
import React from 'react';

export interface ModeChooserOption {
    id: string;
    title: string;
    /** What this mode actually does in this module, in the client's words. */
    blurb: string;
    /** Audience chip, e.g. "Most people start here". */
    chip: string;
}

export function ModeChooser({
    heading = 'How do you want to model your options?',
    value,
    onSelect,
    options,
}: {
    heading?: string | null;
    value: string;
    onSelect: (id: string) => void;
    options: ModeChooserOption[];
}) {
    return (
        <div>
            {heading && <h3 className="text-xl font-bold text-gray-800 mb-4">{heading}</h3>}
            <div className="flex flex-col md:flex-row gap-4 mb-2">
                {options.map((option) => {
                    const selected = value === option.id;
                    return (
                        <button
                            key={option.id}
                            onClick={() => onSelect(option.id)}
                            className={`flex-1 px-6 py-4 rounded-xl border-2 transition-all duration-200 shadow-sm font-semibold text-lg flex flex-col items-center gap-2 ${
                                selected
                                    ? 'border-blue-600 bg-blue-50 text-blue-700 scale-105'
                                    : 'border-gray-200 bg-white text-gray-500 hover:border-blue-400 hover:bg-blue-50'
                            }`}
                            type="button"
                        >
                            <span>{option.title}</span>
                            <span className="text-xs font-normal text-gray-500 leading-snug">{option.blurb}</span>
                            <span className="text-[10px] font-bold uppercase tracking-wide text-blue-700 bg-blue-100 rounded-full px-2.5 py-0.5">
                                {option.chip}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export default ModeChooser;
